 import React, {
  createContext,
  useContext,
  useState,
  useEffect, 
  ReactNode,
} from "react";
import axiosInstance from "../services/axiosInstance";
import type { RolePermissions } from "../types/permissions";
// import { getConsolidatedPermissions } from "../services/userService";
import { getUserPermissions } from "../services/permissionService";

export type UserRole =
  | "super-admin"
  | "admin"
  | "visa-manager"
  | "staff"
  | "volunteer"
  | "guest"
  | "external-agent"
  | "hotel-manager"
  | "tour-operator"
  | "hr-team"
  | "finance-officer"
  | "support-staff";

export interface PermissionRole {
  roleName: string;
}
export interface ModulePermissions {
  moduleScreenId: number;
  canView: boolean;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canApprove: boolean;
  canLock: boolean;
  canRestore: boolean;
  canManageUsers: boolean;
}

// export interface Permissions {
//   roles?: PermissionRole[];
//   [key: string]: unknown;
// }

export interface User {
  id: string | number;
  name: string;
  email: string;
  officeName: string;
  role?: string;
  token?: string;
  expiresAt?: string;
  permissions?: RolePermissions;
  tourOperatorId?: number | null;
  tourOperatorStatus?: string | null;
  department?: string;
  itsNumber?: string;
  mobileNo?: string;
  whatsAppNumber?: string;
  isSuperAdmin?: boolean;
}

export interface UserData {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  department?: string;
  isActive?: boolean;
  permissionExpiryDate?: string; 
  itsNumber?: string; 
  venue?: string; 
}

export interface MenuItem {
  moduleScreenId: number;
  screenCode: string;
  screenName: string;
  label: string;
  icon: string;
  iconColor: string;
  iconBg: string;
  path: string;
  moduleName: string;
}

export interface MenuCategory {
  moduleId: number;
  id: string;
  moduleName: string;
  moduleCode: string;
  label: string;
  color: string;
  items: MenuItem[];
}

interface AuthContextType {
  user: User | null;
  login: (loginId: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  error: string | null;
  clearError: () => void; 
  loading: boolean;
  permissions?: RolePermissions; 
  setAuthenticatedUser: (user: User) => void;
  setDynamicMenuListFun: (menu: MenuCategory[]) => void;
  menuCategoriesList: MenuCategory[];
  verifyOtp: (otp: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};

const STORAGE_KEY_USER = "fhims_auth_user";
const STORAGE_KEY_TOKEN = "fhims_auth_token";
const STORAGE_MENU_KEY = "fhims_menu";



// const getBaseRole = (
//   email: string
// ): "admin" | "Mannat-Finance" | "Mannat-User" => {
//   const lower = email.toLowerCase();
//   if (lower.includes("admin")) return "admin";
//   if (lower.includes("mannatfinance")) return "Mannat-Finance";
//   return "Mannat-User";
// };

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [menuCategoriesList, setMenuCategoriesList] = useState<MenuCategory[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [logoutTimer, setLogoutTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const clearError = () => {
    setError(null);
  };


  useEffect(() => {
  const storedUser = localStorage.getItem("fhims_auth_user");
  const storedToken = localStorage.getItem("fhims_auth_token");
  const storedMenu = localStorage.getItem("fhims_menu");

  if (storedToken) {
  axiosInstance.defaults.headers.common["Authorization"] =
    `Bearer ${storedToken}`;
}

  if (storedUser && storedToken) {
  const parsedUser: User = JSON.parse(storedUser);

  if (parsedUser.expiresAt) {
    const isExpired = Date.now() >= new Date(parsedUser.expiresAt).getTime();

    if (isExpired) {
      logout();
      return;
    }

    scheduleTokenExpiryLogout(parsedUser.expiresAt);
  }

  setUser(parsedUser);

  if (storedMenu) {
    const menuStored = JSON.parse(storedMenu);
    setMenuCategoriesList(menuStored);
  }
}

  setLoading(false);
// eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

useEffect(() => {
  return () => {
    if (logoutTimer) {
      clearTimeout(logoutTimer);
    }
  };
}, [logoutTimer]);

  const setAuthenticatedUser = (userData: User) => {
    setUser(userData);
  };

  const setDynamicMenuListFun = (menu: MenuCategory[]) => {
    setMenuCategoriesList(menu);
  };

  const login = async (loginId: string, password: string) => {
    setError(null);
    setLoading(true);

    try {
      const response = await axiosInstance.post("/Auth/login/init", {
        loginId,
        password,
      });

      if (response.status === 200 && response.data) {
        localStorage.setItem("fhims_loginId", loginId);

        return;
      } else {
        setError("Login failed. Please check your credentials.");
        throw new Error("Invalid response from API");
      }
    } catch (err: unknown) {
      const errorObj = err as { response?: { status?: number }; code?: string };

      if (
        errorObj.response?.status === 400 ||
        errorObj.response?.status === 401
      ) {
        setError("Invalid login ID or password.");
      } else if (errorObj.code === "ERR_NETWORK") {
        setError("Unable to connect to the server.");
      } else {
        setError("Unexpected error during login.");
      }

      throw err;
    } finally {
      setLoading(false);
    }
  };

const verifyOtp = async (otp: string) => {
  setError(null);
  setLoading(true);

  try {
    const loginId = localStorage.getItem("fhims_loginId");

    const response = await axiosInstance.post("/Auth/login/verify-otp", {
      loginId,
      otp,
    });

    const {
      token,
      expiresAt,
      userId,
      email,
      officeName,
      fullName,
      tourOperatorId,
      tourOperatorStatus,
      isSuperAdmin,
    } = response.data;

    localStorage.setItem(STORAGE_KEY_TOKEN, token);

    axiosInstance.defaults.headers.common["Authorization"] =
      `Bearer ${token}`;

    const apiPermissions = await getUserPermissions();

    const formattedPermissions: RolePermissions = {};

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    apiPermissions.forEach((perm: any) => {
      formattedPermissions[perm.moduleScreenId] = {
        view: perm.canView,
        add: perm.canAdd,
        edit: perm.canEdit,
        delete: perm.canDelete,
        approve: perm.canApprove,
        lock: perm.canLock,
        restore: perm.canRestore,
        manageUsers: perm.canManageUsers,
      };
    });

    const fullUser: User = {
      id: userId,
      name: fullName,
      email,
      officeName,
      token,
      expiresAt,
      tourOperatorId: tourOperatorId ?? null,
      tourOperatorStatus: tourOperatorStatus ?? null,
      permissions: formattedPermissions,
      isSuperAdmin,
    };

    localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(fullUser));

    setUser(fullUser);
    scheduleTokenExpiryLogout(expiresAt);

  } catch (err) {
    setError("Invalid OTP");
    throw err;
  } finally {
    setLoading(false);
  }
};

  const logout = () => {
    setUser(null);
    setError(null);
    setMenuCategoriesList([])

    localStorage.removeItem(STORAGE_KEY_USER);
    localStorage.removeItem(STORAGE_KEY_TOKEN);
    localStorage.removeItem(STORAGE_MENU_KEY);
    localStorage.removeItem("fhims_loginId");

    delete axiosInstance.defaults.headers.common["Authorization"];

    window.location.href = "/";
  };

  const scheduleTokenExpiryLogout = (expiresAt: string) => {
    const expiryTime = new Date(expiresAt).getTime() - Date.now();

    if (logoutTimer) {
      clearTimeout(logoutTimer);
    }

    if (expiryTime <= 0) {
      logout();
      return;
    }

    const timer = setTimeout(() => {
      logout();
    }, expiryTime);

    setLogoutTimer(timer);
  };

  return (
    <AuthContext.Provider
      value={{
        menuCategoriesList,
        user,
        login,
        verifyOtp,
        logout,
        isAuthenticated: !!user,
        error,
        clearError,
        loading,
        permissions: user?.permissions,
        setAuthenticatedUser,
        setDynamicMenuListFun
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
